/**
 * Test data collections: named sets of test data values, typically one per environment (Dev,
 * Staging, Preprod). A run picks one collection (or none); its values replace the default values
 * with the same name, so the same tests, groups and flows run against any environment.
 *  - Organization collections (managed like organization test data) are available in every project.
 *    Their values can be organization-wide or set per project (e.g. each project's own Staging address).
 *  - Project collections exist in one project only.
 * Only a collection's creator (or a Project Admin) renames or deletes it; anyone may duplicate it.
 * The reserved name APP_URL replaces the project's app address for the run (web projects).
 */
import { z } from 'zod';

/** Test data name that replaces the project's app address (web projects). */
export const APP_URL_KEY = 'APP_URL';

// The e2e web engine rejects plain HTTP on non-loopback hosts (INVALID_APP_URL), so apps under
// test must be HTTPS unless they run on the runner's own loopback.
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);
export function isAllowedTargetUrl(raw: string): boolean {
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return false;
  }
  if (u.protocol === 'https:') return true;
  return u.protocol === 'http:' && (LOOPBACK_HOSTS.has(u.hostname) || u.hostname.endsWith('.localhost'));
}
export const TARGET_URL_HINT = 'Use an https:// address (plain http only works for localhost)';

/** Why an APP_URL value cannot be used (null = fine, or not APP_URL). */
export function appUrlProblem(key: string, value: string | undefined, isSecret: boolean): string | null {
  if (key !== APP_URL_KEY) return null;
  if (isSecret) return `${APP_URL_KEY} is the app address and cannot be secret`;
  if (value !== undefined && !isAllowedTargetUrl(value.trim())) return `${APP_URL_KEY}: ${TARGET_URL_HINT}`;
  return null;
}

export const collectionInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Enter a name').max(80),
    description: z.string().trim().max(500).nullish(),
  })
  .strict();
export type CollectionInput = z.infer<typeof collectionInputSchema>;

export const collectionUpdateSchema = collectionInputSchema.partial().strict();
export type CollectionUpdate = z.infer<typeof collectionUpdateSchema>;

/** Optional collection for a run ("Run", "Run group", "Run flow", schedules and webhooks). */
export const runCollectionSchema = z.object({ collectionId: z.string().min(1).max(64).nullish() });

export interface CollectionView {
  id: string;
  name: string;
  description: string | null;
  scope: 'ORG' | 'PROJECT';
  /** Values in this collection visible where it was listed (org page: org values; project page: org + this project's). */
  valueCount: number;
  createdBy: { id: string; name: string };
  updatedAt: string;
}

/** One stored value, as needed to merge layers. */
export interface VariableLayerRow {
  key: string;
  projectId: string | null;
  collectionId: string | null;
}

/**
 * Priority of a value for a run (higher wins): default org < default project < collection org <
 * collection project. Rows from other collections are -1 (not used).
 */
export function layerRank(row: VariableLayerRow, collectionId: string | null): number {
  if (row.collectionId && row.collectionId !== collectionId) return -1;
  return (row.collectionId ? 2 : 0) + (row.projectId ? 1 : 0);
}

/** The rows a run uses, one per key: the highest layer wins. */
export function effectiveRows<T extends VariableLayerRow>(rows: T[], collectionId: string | null): Map<string, T> {
  const out = new Map<string, { row: T; rank: number }>();
  for (const row of rows) {
    const rank = layerRank(row, collectionId);
    if (rank < 0) continue;
    const prev = out.get(row.key);
    if (!prev || rank > prev.rank) out.set(row.key, { row, rank });
  }
  return new Map([...out].map(([k, v]) => [k, v.row]));
}

/**
 * key -> isSecret for checking a test when it is saved: every key some run could provide (the
 * defaults alone, or with any collection). A key counts as secret if it is secret in any of those, so
 * the test works with whichever collection a run picks.
 */
export function variablesForAnyCollection<T extends VariableLayerRow & { isSecret: boolean }>(rows: T[]): Map<string, boolean> {
  const choices = [null, ...new Set(rows.flatMap((r) => (r.collectionId ? [r.collectionId] : [])))];
  const out = new Map<string, boolean>();
  for (const c of choices) for (const [key, row] of effectiveRows(rows, c)) out.set(key, Boolean(out.get(key)) || row.isSecret);
  return out;
}
